import { useCallback, useState, useEffect, useRef } from 'react';
import { useAgentSelector, useAgentDispatch } from './useAgentStore';
import {
  setChangeRequestEditingItem,
  setCREditingDialogExpanded,
} from '../app/Slices/changeRequestEditSlice';
import {
  deleteChangeRequestItem,
  addChangeRequestRow,
  editChangeRequestItem,
} from '../app/ActionTypes/changeRequestActionTypes';
import { saveRecord } from '../services/changeRequestService';
import {
  ChangeRequestDirection,
  ChangeRequestSource,
} from '../enums/changeRequest';
import React from 'react';
import { Table } from 'antd';
import {
  Checklist,
  Execution,
  ExecutionItem,
  ChecklistItem,
  RowPosition,
  AddRowItems,
} from '../interfaces/changeRequest';
import { Notify } from '../services/notificationService';
import { setRevokeAPICall } from '../services/copilotThread.service';
import { NOT_FOUND } from '../constants/texts';
import { confirmAction } from '../utils/confirmActionModal';
import { fetchChangeRequests } from '../app/ActionTypes/changeRequestsActions';

export function useChangeRequest<T extends ExecutionItem | ChecklistItem>(
  data: T[] | undefined,
  source: ChangeRequestSource | null,
  loading: boolean,
  sourceEdit: ChangeRequestSource,
  sourcePath: string
) {
  const agentDispatch = useAgentDispatch();
  const changeRequestId = useAgentSelector(
    (state) => state.changeRequestState.changeRequestId
  );
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [navigate, setNavigate] = useState(true);
  const isNewItemRef = useRef(false);
  const tblRef: Parameters<typeof Table>[0]['ref'] = React.useRef(null);

  const navigateToEdit = useCallback(
    (direction: ChangeRequestDirection) => {
      if (navigate) {
        const currentIndex =
          data?.findIndex((i) => i.id === editingItem?.id) ?? -1;
        if (currentIndex === -1 || !data || data.length === 0) return;

        const nextIndex =
          direction === ChangeRequestDirection.NEXT
            ? (currentIndex + 1) % data.length
            : (currentIndex - 1 + data.length) % data.length;

        setEditingItem(data[nextIndex]);
        agentDispatch(setChangeRequestEditingItem(data[nextIndex]));
        tblRef.current?.scrollTo({
          index: nextIndex - 1 >= 0 ? nextIndex - 1 : nextIndex,
        });
      }
      setNavigate(true);
    },
    [editingItem, data, navigate, agentDispatch]
  );

  const handleEditRow = useCallback(
    async (item: T | null) => {
      if (item && isDirty) {
        const confirmLeave = await confirmAction({
          title:
            'You have unsaved changes. Are you sure you want to edit a different row?',
        });
        if (!confirmLeave) return;
      }
      setRevokeAPICall(true);
      setEditingItem(item);
      agentDispatch(setChangeRequestEditingItem(item));
      agentDispatch(setCREditingDialogExpanded(!!item));
    },
    [isDirty]
  );

  /**
   * Delete row from change request and update the data
   * @param item - The item to delete
   */
  const handleDeleteRow = useCallback(
    async (item: T) => {
      const index = data?.findIndex((i) => i.id === item.id);
      if (index === undefined || index === -1) {
        Notify.error(NOT_FOUND);
        return;
      }
      const isConfirmed = await confirmAction({
        title: 'Are you sure you want to delete this row?',
        okText: 'Delete',
        okType: 'danger',
      });
      if (!isConfirmed) return;

      const crId = changeRequestId;
      if (!crId) {
        Notify.error('No change request selected');
        return;
      }

      try {
        const response = await agentDispatch(
          deleteChangeRequestItem({
            path: `${sourcePath}/items/${index}`,
            itemId: item.id,
            crId,
            source: sourceEdit,
          })
        );
        if (response.type.endsWith('/rejected')) {
          Notify.error('Error deleting item');
        } else {
          handleEditRow(null);
        }
      } catch (error) {
        Notify.error('Error deleting item');
      }
    },
    [data]
  );

  const handleExpandToFullScreen = useCallback((item: T) => {
    setEditingItem(item);
    agentDispatch(setChangeRequestEditingItem(item));
  }, []);

  const saveItem = useCallback(
    async (updatedItem: T, navigate = true) => {
      if (!data) {
        Notify.error('Something went wrong. Please try again.');
        return;
      }
      const index = data?.findIndex((i) => i.id === updatedItem.id);
      if (index === undefined || index === -1) {
        Notify.error(NOT_FOUND);
        return;
      }
      if (isNewItemRef.current) {
        const updatedData = [...data];
        updatedData[index] = updatedItem;
        await updateDataArray({
          items: updatedData as ChecklistItem[] | ExecutionItem[],
        });
        isNewItemRef.current = false;
        handleEditRow(null);
      }

      setNavigate(navigate);

      return await saveRecord(
        agentDispatch,
        index,
        updatedItem,
        sourceEdit,
        sourcePath
      );
    },
    [data]
  );

  const handleAddRow = useCallback(
    async (item: T, position: RowPosition, newItem: AddRowItems) => {
      if (!data) return;
      const currentIndex = data.findIndex((i) => i.id === item.id);
      if (currentIndex === -1) return;
      const crId = changeRequestId;
      if (!crId) {
        Notify.error('No change request selected');
        return;
      }
      const newIndex =
        position === RowPosition.ABOVE ? currentIndex : currentIndex + 1;
      try {
        await agentDispatch(
          addChangeRequestRow({
            path: sourcePath,
            updatedValues: newItem,
            crId,
            source: sourceEdit,
            direction: position,
            fromIndex: currentIndex,
            toIndex: newIndex,
          })
        );
        isNewItemRef.current = true;
        setEditingItem(newItem as T);
        agentDispatch(setChangeRequestEditingItem(newItem as T));
        agentDispatch(setCREditingDialogExpanded(true));
      } catch (error) {
        Notify.error('Error adding new item');
      }
    },
    [data, agentDispatch, sourcePath, sourceEdit]
  );

  /**
   * Move row to a specific index in change request and update the data
   * @param draggedId - The id of the dragged item
   * @param newIndex - The target index where the row will be moved
   */
  const updateDataArray = useCallback(
    async (updatedItems: { items: ChecklistItem[] | ExecutionItem[] }) => {
      const crId = changeRequestId;
      if (!crId) {
        Notify.error('No change request selected');
        return;
      }

      try {
        await agentDispatch(
          editChangeRequestItem({
            path: `${sourcePath}`,
            updatedItem: updatedItems as Checklist | Execution,
            crId,
            source: sourceEdit,
          })
        );
        await agentDispatch(fetchChangeRequests());
      } catch (error) {
        Notify.error('Error updating data');
      }
    },
    [agentDispatch, data, sourcePath, sourceEdit]
  );

  useEffect(() => {
    if (source !== ChangeRequestSource.ACTIONS_IMPLEMENT_ONE) {
      if (editingItem && source === sourceEdit && data && !loading) {
        // Use a ref to avoid circular dependency
        const currentIndex =
          data.findIndex((i) => i.id === editingItem.id) ?? -1;
        if (currentIndex !== -1 && data.length > 0) {
          const nextIndex = (currentIndex + 1) % data.length;
          setEditingItem(data[nextIndex]);
          agentDispatch(setChangeRequestEditingItem(data[nextIndex]));
          tblRef.current?.scrollTo({
            index: nextIndex - 1 >= 0 ? nextIndex - 1 : nextIndex,
          });
        }
      } else if (editingItem && source !== sourceEdit) {
        setEditingItem(null);
        agentDispatch(setChangeRequestEditingItem(null));
        agentDispatch(setCREditingDialogExpanded(false));
      }
    }
  }, [source, data, editingItem, sourceEdit, loading, agentDispatch]);

  return {
    editingItem,
    isDirty,
    setIsDirty,
    navigateToEdit,
    handleEditRow,
    handleAddRow,
    handleDeleteRow,
    updateDataArray,
    saveItem,
    tblRef,
    handleExpandToFullScreen,
  };
}
